/**
 * PurgeHistoryViewer — read-only mirror of the SuperAdmin Records Mgmt
 * audit log (`/v3/super-admin/records/purge-history`).
 */

import { useCallback, useEffect, useState } from 'react';
import type { PurgeHistoryRow } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { downloadCsv, formatLocal, rowsToCsv } from '@/utils/time';

type DateRange = '7d' | '30d' | '90d' | 'ytd';

function startDateFor(range: DateRange): string {
  const d = new Date();
  if (range === '7d') d.setDate(d.getDate() - 7);
  else if (range === '30d') d.setDate(d.getDate() - 30);
  else if (range === '90d') d.setDate(d.getDate() - 90);
  else d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function PurgeHistoryViewer(): JSX.Element {
  const api = useApiClient();
  const [range, setRange] = useState<DateRange>('30d');
  const [entity, setEntity] = useState<string>('');
  const [rows, setRows] = useState<PurgeHistoryRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const filters: { startDate?: string; entityType?: string; top?: number } = {
        startDate: startDateFor(range),
        top: 500,
      };
      if (entity) filters.entityType = entity;
      const resp = await api.retail.superAdmin.records.purgeHistory(filters);
      setRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purge history');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, range, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = (): void => {
    if (rows.length === 0) return;
    const csv = rowsToCsv(rows as unknown as ReadonlyArray<Record<string, unknown>>);
    downloadCsv(`purge-history-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <section className="card">
      <header className="card-header">
        <h3>Purge history</h3>
        <div className="toolbar__spacer" />
        <label className="toolbar__field">
          Range
          <select value={range} onChange={(e) => setRange(e.target.value as DateRange)}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="ytd">Year to date</option>
          </select>
        </label>
        <label className="toolbar__field">
          Entity
          <select value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">All</option>
            <option value="Cart">Cart</option>
            <option value="CartProduct">CartProduct</option>
            <option value="MaintAction">MaintAction</option>
          </select>
        </label>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void load()}
        >
          Refresh
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </button>
      </header>

      {loading && <p>Loading…</p>}
      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="empty">No purge history rows in this window.</div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Purged at</th>
              <th>By</th>
              <th>Entity</th>
              <th>Key</th>
              <th>Reason</th>
              <th>Recovered?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.purgeHistoryId}>
                <td>{formatLocal(r.purgedAt)}</td>
                <td>{r.purgedByDisplayName ?? r.purgedByOfficerCode ?? '—'}</td>
                <td>{r.entityType}</td>
                <td>
                  <code>{r.entityKeyHumanized ?? r.entityKey}</code>
                </td>
                <td>
                  {r.reasonCodeLabel ?? r.reasonCode}
                  {r.reason ? ` — ${r.reason}` : ''}
                </td>
                <td>
                  {r.isRecovered ? (
                    <span className="pill pill--ok">Recovered</span>
                  ) : (
                    <span className="pill pill--muted">No</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
